/**
 * NVV Audio Player — Nothing Ventured Vinyl
 * =========================================
 * - Play buttons on collection cards (small overlay)
 * - Tracklist rows on product pages (numbered list under image)
 * - Persistent bottom player bar with next/prev, progress, seek
 * - Add to cart from player bar
 * - Skip ±10s buttons with "10" label (Cold Cuts style)
 * - Mobile: two-row layout with progress bar visible
 */

(function () {
  'use strict';

  // ── State ──────────────────────────────────────────────────
  let currentAudio     = null;
  let isPlaying        = false;
  let rafId            = null;

  // Active row tracking
  let activeTrackRow   = null;   // the .nvv-track-row button currently playing
  let activeCardBtn    = null;   // the .nvv-card-play-btn currently playing
  let activeTracklist  = null;   // the .nvv-tracklist container

  // Queue (built from a tracklist or a single card)
  let queue            = [];     // [{ url, name }]
  let queueIndex       = 0;
  let queueArtist      = '';
  let queueImage       = '';
  let queueProductUrl  = '#';
  let queueVariantId   = '';     // for add-to-cart

  // ── Build player bar ──────────────────────────────────────
  function buildPlayerBar() {
    if (document.getElementById('nvv-player-bar')) return;

    const bar = document.createElement('div');
    bar.id = 'nvv-player-bar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Audio preview player');

    bar.innerHTML = `
      <!-- ROW A: info (desktop: merges with B via display:contents) -->
      <div id="nvv-bar-row-a">
        <img id="nvv-bar-thumb" src="" alt="" aria-hidden="true" />
        <div id="nvv-bar-info">
          <div id="nvv-bar-title">—</div>
          <div id="nvv-bar-artist"></div>
          <div id="nvv-bar-track-counter"></div>
        </div>
        <button class="nvv-bar-btn" id="nvv-bar-collapse-btn" aria-label="Expand player" type="button">
          <svg id="nvv-bar-chevron" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
          </svg>
        </button>
        <button class="nvv-bar-btn" id="nvv-bar-close-mobile" aria-label="Close player" type="button">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <!-- ROW B: controls (desktop: inline after info) -->
      <div id="nvv-bar-row-b">
        <button class="nvv-bar-btn" id="nvv-bar-prev" aria-label="Previous track" type="button" disabled>
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/>
          </svg>
        </button>
        <button class="nvv-bar-btn nvv-bar-skip-btn" id="nvv-bar-rewind" aria-label="Rewind 10s" type="button">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
          </svg>
          <span class="nvv-bar-skip-label">10</span>
        </button>
        <button class="nvv-bar-btn" id="nvv-bar-play-pause" aria-label="Play" type="button">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path id="nvv-pp-path" d="M8 5.14v14l11-7-11-7z"/>
          </svg>
        </button>
        <button class="nvv-bar-btn nvv-bar-skip-btn" id="nvv-bar-forward" aria-label="Forward 10s" type="button">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
          </svg>
          <span class="nvv-bar-skip-label">10</span>
        </button>
        <button class="nvv-bar-btn" id="nvv-bar-next" aria-label="Next track" type="button" disabled>
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6 18l8.5-6L6 6v12zm2-8.14 5.51 3.89L8 17.14V9.86zM16 6h2v12h-2z"/>
          </svg>
        </button>
        <button id="nvv-bar-cart-btn" aria-label="Add to cart" type="button" title="Add to cart">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path id="nvv-cart-icon-path" d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            <circle cx="10" cy="20.5" r="1.5" fill="currentColor" stroke="none"/>
            <circle cx="18" cy="20.5" r="1.5" fill="currentColor" stroke="none"/>
          </svg>
        </button>
        <a id="nvv-bar-view-link" href="#">View</a>
        <button class="nvv-bar-btn" id="nvv-bar-close" aria-label="Close player" type="button">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <!-- ROW C: progress bar (all breakpoints) -->
      <div id="nvv-bar-row-c">
        <span id="nvv-bar-time-current">0:00</span>
        <input type="range" id="nvv-bar-progress" min="0" max="100" value="0" step="0.1"
          aria-label="Seek" style="--progress: 0%;" />
        <span id="nvv-bar-time-total">0:00</span>
      </div>
    `;

    document.body.appendChild(bar);

    document.getElementById('nvv-bar-play-pause').addEventListener('click', togglePlayPause);
    document.getElementById('nvv-bar-rewind').addEventListener('click', () => seekRelative(-10));
    document.getElementById('nvv-bar-forward').addEventListener('click', () => seekRelative(10));
    document.getElementById('nvv-bar-prev').addEventListener('click', playPrev);
    document.getElementById('nvv-bar-next').addEventListener('click', playNext);
    document.getElementById('nvv-bar-close').addEventListener('click', closePlayer);
    document.getElementById('nvv-bar-close-mobile').addEventListener('click', closePlayer);
    document.getElementById('nvv-bar-cart-btn').addEventListener('click', addToCartFromBar);
    document.getElementById('nvv-bar-collapse-btn').addEventListener('click', toggleBarCollapse);

    const prog = document.getElementById('nvv-bar-progress');
    prog.addEventListener('input', onProgressInput);
    prog.addEventListener('change', onProgressChange);

    // Desktop mouse scrubbing — explicit handlers to ensure clicks register
    let mouseDown = false;
    prog.addEventListener('mousedown', (e) => {
      mouseDown = true;
      e.stopPropagation();
      const rect = prog.getBoundingClientRect();
      const pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const val  = pct * (parseFloat(prog.max) || 100);
      prog.value = val;
      prog.style.setProperty('--progress', (pct * 100) + '%');
      document.getElementById('nvv-bar-time-current').textContent = formatTime(val);
      if (currentAudio) currentAudio.currentTime = val;
    });
    document.addEventListener('mousemove', (e) => {
      if (!mouseDown) return;
      const rect = prog.getBoundingClientRect();
      const pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const val  = pct * (parseFloat(prog.max) || 100);
      prog.value = val;
      prog.style.setProperty('--progress', (pct * 100) + '%');
      document.getElementById('nvv-bar-time-current').textContent = formatTime(val);
      if (currentAudio) currentAudio.currentTime = val;
    });
    document.addEventListener('mouseup', () => { mouseDown = false; });

    // Mobile touch scrubbing — range inputs need explicit touch handling on iOS/Android
    prog.addEventListener('touchstart', (e) => { e.stopPropagation(); }, { passive: true });
    prog.addEventListener('touchmove', (e) => {
      e.stopPropagation();
      const touch = e.touches[0];
      const rect  = prog.getBoundingClientRect();
      const pct   = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
      const val   = pct * (parseFloat(prog.max) || 100);
      prog.value  = val;
      prog.style.setProperty('--progress', (pct * 100) + '%');
      document.getElementById('nvv-bar-time-current').textContent = formatTime(val);
    }, { passive: true });
    prog.addEventListener('touchend', () => {
      if (currentAudio) currentAudio.currentTime = parseFloat(prog.value);
    }, { passive: true });

    // No JS layout needed — CSS handles desktop/mobile via display:contents and media queries
  }

  // ── Load from a tracklist row ─────────────────────────────
  function loadFromTrackRow(row) {
    const tracklist = row.closest('.nvv-tracklist');
    const rows      = Array.from(tracklist.querySelectorAll('.nvv-track-row'));
    const index     = rows.indexOf(row);

    // Same tracklist — just jump to track (or toggle if same row)
    if (activeTracklist === tracklist) {
      if (activeTrackRow === row && currentAudio) {
        togglePlayPause();
        return;
      }
      queueIndex = index;
      playCurrentFromQueue();
      return;
    }

    // New tracklist
    stopAll(false);
    activeTracklist = tracklist;

    queue = rows.map(r => ({
      url:  r.dataset.audioUrl  || '',
      name: r.dataset.trackName || '',
    }));

    queueIndex      = index;
    queueArtist     = tracklist.dataset.productArtist  || '';
    queueImage      = tracklist.dataset.productImage   || '';
    queueProductUrl = tracklist.dataset.productUrl     || '#';
    queueVariantId  = tracklist.dataset.productVariant || '';

    playCurrentFromQueue();
  }

  // ── Load from a collection card button ───────────────────
  function getQueueFromButton(btn) {
    const tracks = [];

    for (let i = 1; i <= 30; i++) {
      const url = btn.getAttribute('data-track-' + i + '-url');
      const name = btn.getAttribute('data-track-' + i + '-name');
      if (url) tracks.push({ url: url, name: name || '' });
    }

    if (!tracks.length && btn.dataset.audioUrl) {
      tracks.push({ url: btn.dataset.audioUrl, name: btn.dataset.trackName || '' });
    }

    return tracks;
  }

  function loadFromCardBtn(btn) {
    if (activeCardBtn === btn && currentAudio) {
      togglePlayPause();
      return;
    }

    stopAll(false);
    activeCardBtn = btn;

    queue = getQueueFromButton(btn);
    queueIndex      = 0;
    queueArtist     = btn.dataset.productArtist  || '';
    queueImage      = btn.dataset.productImage   || '';
    queueProductUrl = btn.dataset.productUrl     || '#';
    queueVariantId  = btn.dataset.productVariant || '';

    playCurrentFromQueue();
  }

  // ── Core: play queue[queueIndex] ─────────────────────────
  function playCurrentFromQueue() {
    const track = queue[queueIndex];
    if (!track || !track.url) return;

    stopAudio();

    currentAudio = new Audio(track.url);
    currentAudio.preload = 'auto';

    updateBarInfo(track.name, queueArtist, queueImage, queueProductUrl);
    updateTrackCounter();
    updatePrevNext();
    highlightActiveRow();
    showBar();
    setBarPlayState(true);
    setCardBtnPlaying(activeCardBtn, true);
    syncImageOverlayBtn(true);

    currentAudio.addEventListener('loadedmetadata', () => {
      document.getElementById('nvv-bar-time-total').textContent = formatTime(currentAudio.duration);
      document.getElementById('nvv-bar-progress').max = currentAudio.duration || 100;
    });

    currentAudio.addEventListener('ended', onEnded);
    currentAudio.addEventListener('error', onError);

    currentAudio.play().then(() => {
      isPlaying = true;
      startRaf();
    }).catch(console.warn);
  }

  // ── Next / Prev ────────────────────────────────────────────
  function playNext() {
    if (queueIndex < queue.length - 1) {
      queueIndex++;
      playCurrentFromQueue();
    }
  }

  function playPrev() {
    if (currentAudio && currentAudio.currentTime > 3) {
      currentAudio.currentTime = 0;
    } else if (queueIndex > 0) {
      queueIndex--;
      playCurrentFromQueue();
    }
  }

  function onError() { stopAll(true); }

  function onEnded() {
    isPlaying = false;
    cancelRaf();
    unhighlightRows();
    if (queueIndex < queue.length - 1) {
      queueIndex++;
      playCurrentFromQueue();
    } else {
      setBarPlayState(false);
      setCardBtnPlaying(activeCardBtn, false);
      resetProgress();
    }
  }

  // ── Toggle ────────────────────────────────────────────────
  function togglePlayPause() {
    if (!currentAudio) return;
    if (isPlaying) {
      currentAudio.pause();
      isPlaying = false;
      setBarPlayState(false);
      setActiveRowPlaying(false);
      setCardBtnPlaying(activeCardBtn, false);
      syncImageOverlayBtn(false);
      cancelRaf();
    } else {
      currentAudio.play().then(() => {
        isPlaying = true;
        setBarPlayState(true);
        setActiveRowPlaying(true);
        setCardBtnPlaying(activeCardBtn, true);
        syncImageOverlayBtn(true);
        startRaf();
      }).catch(console.warn);
    }
  }

  function seekRelative(delta) {
    if (!currentAudio) return;
    currentAudio.currentTime = Math.max(0, Math.min(currentAudio.duration || 0, currentAudio.currentTime + delta));
    updateProgressBar();
  }

  function onProgressInput() {
    const prog = document.getElementById('nvv-bar-progress');
    const pct  = (prog.value / (prog.max || 1)) * 100;
    prog.style.setProperty('--progress', pct + '%');
    document.getElementById('nvv-bar-time-current').textContent = formatTime(parseFloat(prog.value));
  }

  function onProgressChange() {
    if (currentAudio) currentAudio.currentTime = parseFloat(document.getElementById('nvv-bar-progress').value);
  }

  // ── RAF progress ──────────────────────────────────────────
  function startRaf() {
    cancelRaf();
    function tick() { updateProgressBar(); rafId = requestAnimationFrame(tick); }
    rafId = requestAnimationFrame(tick);
  }

  function cancelRaf() {
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
  }

  function updateProgressBar() {
    if (!currentAudio || !currentAudio.duration) return;
    const prog = document.getElementById('nvv-bar-progress');
    const t = currentAudio.currentTime, d = currentAudio.duration;
    prog.value = t;
    prog.style.setProperty('--progress', ((t / d) * 100) + '%');
    document.getElementById('nvv-bar-time-current').textContent = formatTime(t);
  }

  function resetProgress() {
    const prog = document.getElementById('nvv-bar-progress');
    prog.value = 0;
    prog.style.setProperty('--progress', '0%');
    document.getElementById('nvv-bar-time-current').textContent = '0:00';
  }

  // ── Stop helpers ──────────────────────────────────────────
  function stopAudio() {
    if (currentAudio) {
      currentAudio.removeEventListener('ended', onEnded);
      currentAudio.removeEventListener('error', onError);
      currentAudio.pause();
      currentAudio.src = '';
      currentAudio = null;
    }
    isPlaying = false;
    cancelRaf();
  }

  function stopAll(hide) {
    stopAudio();
    unhighlightRows();
    setCardBtnPlaying(activeCardBtn, false);
    setBarPlayState(false);
    syncImageOverlayBtn(false);
    activeTrackRow  = null;
    activeCardBtn   = null;
    activeTracklist = null;
    if (hide) hideBar();
  }

  function closePlayer() { stopAll(true); }

  // ── Row highlight ─────────────────────────────────────────
  function highlightActiveRow() {
    unhighlightRows();
    if (!activeTracklist) return;
    const rows = activeTracklist.querySelectorAll('.nvv-track-row');
    activeTrackRow = rows[queueIndex] || null;
    setActiveRowPlaying(true);
  }

  function setActiveRowPlaying(playing) {
    if (!activeTrackRow) return;
    if (playing) {
      activeTrackRow.classList.add('is-playing');
      activeTrackRow.setAttribute('aria-label', 'Pause ' + (activeTrackRow.dataset.trackName || ''));
    } else {
      activeTrackRow.classList.remove('is-playing');
      activeTrackRow.setAttribute('aria-label', 'Play ' + (activeTrackRow.dataset.trackName || ''));
    }
  }

  function unhighlightRows() {
    if (!activeTracklist) return;
    activeTracklist.querySelectorAll('.nvv-track-row').forEach(r => {
      r.classList.remove('is-playing');
    });
    activeTrackRow = null;
  }

  // ── Card / image overlay button state ────────────────────
  function setCardBtnPlaying(btn, playing) {
    if (!btn) return;
    if (playing) btn.classList.add('is-playing');
    else btn.classList.remove('is-playing');
  }

  // Sync the image overlay button with tracklist play state
  function syncImageOverlayBtn(playing) {
    const btn = document.querySelector('.nvv-image-play-btn');
    if (!btn) return;
    const icon = btn.querySelector('#nvv-image-pp-path');
    if (playing) {
      btn.classList.add('is-playing');
      btn.setAttribute('aria-label', 'Pause preview');
      if (icon) icon.setAttribute('d', 'M6 19h4V5H6v14zm8-14v14h4V5h-4z');
    } else {
      btn.classList.remove('is-playing');
      btn.setAttribute('aria-label', 'Preview');
      if (icon) icon.setAttribute('d', 'M8 5.14v14l11-7-11-7z');
    }
  }

  // ── Bar helpers ───────────────────────────────────────────
  function showBar() {
    const b = document.getElementById('nvv-player-bar');
    if (!b) return;
    // Always open expanded — user can collapse manually via chevron
    b.classList.remove('is-collapsed');
    const chevron = document.getElementById('nvv-bar-chevron');
    if (chevron) chevron.querySelector('path').setAttribute('d', 'M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z');
    const btn = document.getElementById('nvv-bar-collapse-btn');
    if (btn) btn.setAttribute('aria-label', 'Collapse player');
    b.classList.add('is-visible');
  }
  function hideBar()  { const b = document.getElementById('nvv-player-bar'); if (b) b.classList.remove('is-visible'); }

  function toggleBarCollapse() {
    // Only operates on mobile — on desktop this button is hidden
    const bar = document.getElementById('nvv-player-bar');
    const chevron = document.getElementById('nvv-bar-chevron');
    if (!bar) return;
    const collapsed = bar.classList.toggle('is-collapsed');
    // Chevron points up when expanded (chevron-up path), down when collapsed (chevron-down path)
    if (chevron) {
      chevron.querySelector('path').setAttribute('d',
        collapsed
          ? 'M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z'  // chevron-down = tap to expand
          : 'M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z'  // chevron-up = tap to collapse
      );
    }
    // Update aria-label
    const btn = document.getElementById('nvv-bar-collapse-btn');
    if (btn) btn.setAttribute('aria-label', collapsed ? 'Expand player' : 'Collapse player');
  }

  function updateBarInfo(title, artist, image, productUrl) {
    document.getElementById('nvv-bar-title').textContent  = title;
    document.getElementById('nvv-bar-artist').textContent = artist;
    const thumb = document.getElementById('nvv-bar-thumb');
    if (image) { thumb.src = image; thumb.classList.add('has-image'); }
    else { thumb.src = ''; thumb.classList.remove('has-image'); }
    const link = document.getElementById('nvv-bar-view-link');
    if (link) link.href = productUrl;
    resetProgress();
    document.getElementById('nvv-bar-time-total').textContent = '0:00';
    resetCartBtn();
  }

  function setBarPlayState(playing) {
    const btn  = document.getElementById('nvv-bar-play-pause');
    const path = document.getElementById('nvv-pp-path');
    if (!btn || !path) return;
    if (playing) {
      path.setAttribute('d', 'M6 19h4V5H6v14zm8-14v14h4V5h-4z');
      btn.setAttribute('aria-label', 'Pause');
    } else {
      path.setAttribute('d', 'M8 5.14v14l11-7-11-7z');
      btn.setAttribute('aria-label', 'Play');
    }
  }

  function updatePrevNext() {
    const prev = document.getElementById('nvv-bar-prev');
    const next = document.getElementById('nvv-bar-next');
    if (!prev || !next) return;
    prev.disabled = queue.length <= 1 || queueIndex === 0;
    next.disabled = queue.length <= 1 || queueIndex === queue.length - 1;
  }

  function updateTrackCounter() {
    const el = document.getElementById('nvv-bar-track-counter');
    if (!el) return;
    el.textContent = queue.length > 1 ? 'Track ' + (queueIndex + 1) + ' of ' + queue.length : '';
  }

  function formatTime(secs) {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60), s = Math.floor(secs % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  // ── Add to cart from player bar ───────────────────────────
  function addToCartFromBar() {
    const variantId = queueVariantId;
    if (!variantId) {
      // No variant id — link to product page instead
      if (queueProductUrl && queueProductUrl !== '#') {
        window.location.href = queueProductUrl;
      }
      return;
    }

    const btn = document.getElementById('nvv-bar-cart-btn');
    if (!btn || btn.dataset.adding) return;
    btn.dataset.adding = '1';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(r => r.json())
    .then(() => {
      // Show checkmark
      const svg = btn.querySelector('svg');
      if (svg) svg.innerHTML = '<path d="M20 6L9 17l-5-5" stroke-width="2.5"/>';
      btn.classList.add('added');

      // Update cart bubble count
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          document.querySelectorAll('[data-cart-count], .cart-count-bubble span, #CartCount').forEach(el => {
            el.textContent = cart.item_count;
          });
          // Dawn theme uses a custom event
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(() => {});

      // Revert after 2.5s
      setTimeout(() => {
        resetCartBtn();
        delete btn.dataset.adding;
      }, 2500);
    })
    .catch(() => {
      delete btn.dataset.adding;
    });
  }

  function resetCartBtn() {
    const btn = document.getElementById('nvv-bar-cart-btn');
    if (!btn) return;
    btn.classList.remove('added');
    delete btn.dataset.adding;
    const svg = btn.querySelector('svg');
    if (svg) svg.innerHTML = '<path id="nvv-cart-icon-path" d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/><circle cx="10" cy="20.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="18" cy="20.5" r="1.5" fill="currentColor" stroke="none"/>';
  }

  // ── Bind buttons ──────────────────────────────────────────
  function bindButtons() {
    // Tracklist rows
    document.querySelectorAll('.nvv-track-row').forEach(row => {
      if (row.dataset.nvvBound) return;
      row.dataset.nvvBound = '1';
      row.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); loadFromTrackRow(row); });
    });

    // Collection card buttons
    document.querySelectorAll('.nvv-card-play-btn').forEach(btn => {
      if (btn.dataset.nvvBound) return;
      btn.dataset.nvvBound = '1';
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); loadFromCardBtn(btn); });
    });

    // Product image overlay button — plays track 1, loading full tracklist queue if present
    document.querySelectorAll('.nvv-image-play-btn').forEach(btn => {
      if (btn.dataset.nvvBound) return;
      btn.dataset.nvvBound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // If toggling same button while playing
        if (activeCardBtn === btn && currentAudio) {
          togglePlayPause();
          return;
        }
        // Try to load full tracklist if one exists on the page
        const tracklist = document.querySelector('.nvv-tracklist');
        if (tracklist) {
          const firstRow = tracklist.querySelector('.nvv-track-row');
          if (firstRow) {
            loadFromTrackRow(firstRow);
            return;
          }
        }
        // Fallback: single track from button data
        loadFromCardBtn(btn);
      });
    });
  }

  // ── Init ──────────────────────────────────────────────────
  function init() {
    buildPlayerBar();
    bindButtons();
    new MutationObserver(() => bindButtons()).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // ── Public API ────────────────────────────────────────────
  window.nvvPlayCard = function (btn) { loadFromCardBtn(btn); };

})();
